import React, { useEffect, useState } from 'react';
import { legalVaultService } from '../services/legalVaultService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiUser, FiFileText, FiCopy, FiExternalLink } = FiIcons;

export default function AuditTrail({ documentId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadActivity = async () => {
      setLoading(true);
      const [auditResult, versionsResult, documentResult] = await Promise.allSettled([
        legalVaultService.getAuditTrail(documentId),
        legalVaultService.getVersions(documentId),
        legalVaultService.getDocumentById(documentId),
      ]);

      const auditEntries = auditResult.status === 'fulfilled' && Array.isArray(auditResult.value) ? auditResult.value : [];
      const versions = versionsResult.status === 'fulfilled' && Array.isArray(versionsResult.value) ? versionsResult.value : [];
      const document = documentResult.status === 'fulfilled' ? documentResult.value : null;
      const versionByNumber = new Map(versions.filter((version) => version.versionNumber).map((version) => [version.versionNumber, version]));
      const loggedPublishedVersions = new Set(
        auditEntries
          .filter((entry) => entry.action === 'Version Published' && entry.version)
          .map((entry) => entry.version)
      );

      const enrichedAuditEntries = auditEntries.map((entry) => {
        const relatedVersion = entry.version ? versionByNumber.get(entry.version) : null;
        return {
          ...entry,
          versionId: relatedVersion?.id || '',
          contentHash: relatedVersion?.contentHash || entry.details?.contentHash || '',
          isPermanentVersion:
            relatedVersion?.isImmutable === true &&
            (relatedVersion?.status === 'Published' || relatedVersion?.status === 'Superseded'),
        };
      });

      const verifiedPublicationEntries = versions
        .filter((version) => {
          if (loggedPublishedVersions.has(version.versionNumber)) return false;
          if (version.status !== 'Published' && version.status !== 'Superseded') return false;
          if (version.isImmutable !== true) return false;

          const publication = version.metadata?.publication;
          if (!publication?.publishedAt || !publication?.contentHash || !version.contentHash) return false;
          if (publication.contentHash !== version.contentHash) return false;

          const storedPublishedAt = version.publishedAt || version.publishedDate || '';
          if (storedPublishedAt && publication.publishedAt !== storedPublishedAt) return false;
          return true;
        })
        .map((version) => {
          const publication = version.metadata.publication;
          return {
            id: `verified-publication-${version.id}`,
            timestamp: publication.publishedAt,
            action: 'Version Published',
            documentTitle: document?.title || '',
            version: version.versionNumber || '',
            versionId: version.id,
            contentHash: version.contentHash,
            isPermanentVersion: true,
            actor: publication.actorName || publication.actorEmail || 'BPA Admin',
            details: {
              contentHash: version.contentHash,
              source: 'verified_version_metadata',
            },
          };
        });

      const mergedEntries = [...enrichedAuditEntries, ...verifiedPublicationEntries].sort(
        (a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
      );

      if (!cancelled) {
        setEntries(mergedEntries);
        setLoading(false);
      }
    };

    loadActivity();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const getActionIcon = (action) => {
    const icons = {
      'Document Created': FiFileText,
      'Draft Saved': FiFileText,
      'Version Published': FiFileText,
      'Version Scheduled': FiClock,
      'Version Archived': FiFileText,
      'Version Withdrawn': FiFileText,
      'Version Duplicated': FiFileText,
      'Archived Version Restored as Draft': FiFileText,
      'Document Duplicated': FiFileText,
      'Template Used': FiFileText,
      'Public Link Copied': FiFileText,
      'Document Exported': FiFileText,
      'Settings Updated': FiFileText,
    };
    return icons[action] || FiFileText;
  };

  const getActionColor = (action) => {
    const colors = {
      'Document Created': 'text-blue-600 bg-blue-50',
      'Draft Saved': 'text-slate-600 bg-slate-50',
      'Version Published': 'text-emerald-600 bg-emerald-50',
      'Version Scheduled': 'text-amber-600 bg-amber-50',
      'Version Archived': 'text-gray-600 bg-gray-50',
      'Version Withdrawn': 'text-red-600 bg-red-50',
      'Version Duplicated': 'text-purple-600 bg-purple-50',
      'Archived Version Restored as Draft': 'text-blue-600 bg-blue-50',
      'Document Duplicated': 'text-purple-600 bg-purple-50',
      'Template Used': 'text-indigo-600 bg-indigo-50',
      'Public Link Copied': 'text-cyan-600 bg-cyan-50',
      'Document Exported': 'text-teal-600 bg-teal-50',
      'Settings Updated': 'text-slate-600 bg-slate-50',
    };
    return colors[action] || 'text-slate-600 bg-slate-50';
  };

  const copyHash = async (hash) => {
    if (!hash) return;
    try {
      await navigator.clipboard.writeText(hash);
      setCopyStatus('SHA-256 hash copied.');
    } catch {
      setCopyStatus('Unable to copy hash.');
    }
    window.setTimeout(() => setCopyStatus(''), 2500);
  };

  const openPermanentVersion = (entry) => {
    if (!entry.versionId || !entry.isPermanentVersion) return;
    window.open(`${window.location.origin}/#/legal/version/${entry.versionId}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="p-4 text-center text-slate-500">Loading audit trail...</div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-slate-800">Activity Log</h3>
        {copyStatus && <span className="text-xs font-medium text-emerald-700">{copyStatus}</span>}
      </div>
      <div className="max-h-96 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <SafeIcon icon={FiFileText} className="text-3xl mx-auto mb-3 text-slate-300" />
            No activity recorded yet.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {entries.map((entry) => (
              <div key={entry.id} className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActionColor(entry.action)}`}>
                    <SafeIcon icon={getActionIcon(entry.action)} className="text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900">
                        {entry.action}
                        {entry.documentTitle && (
                          <span className="ml-2 text-slate-600">
                            on <span className="font-semibold">{entry.documentTitle}</span>
                          </span>
                        )}
                        {entry.version && (
                          <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded">{entry.version}</span>
                        )}
                      </p>
                      <span className="text-xs text-slate-500 flex items-center flex-shrink-0">
                        <SafeIcon icon={FiClock} className="mr-1" />
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-AU') : 'Time unavailable'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2 text-xs text-slate-500">
                      <span className="flex items-center"><SafeIcon icon={FiUser} className="mr-1" />{entry.actor}</span>
                      {entry.contentHash && (
                        <button type="button" onClick={() => copyHash(entry.contentHash)} className="inline-flex items-center px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:text-blue-700 hover:border-blue-200 transition-colors" title="Copy SHA-256 integrity hash">
                          <SafeIcon icon={FiCopy} className="mr-1" /> Copy Hash
                        </button>
                      )}
                      {entry.isPermanentVersion && entry.versionId && (
                        <button type="button" onClick={() => openPermanentVersion(entry)} className="inline-flex items-center px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:text-emerald-700 hover:border-emerald-200 transition-colors" title="Open permanent immutable version">
                          <SafeIcon icon={FiExternalLink} className="mr-1" /> Open Version
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}